import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { DrawnCard, SpreadType } from '../lib/types';
import { usePaidFlowNavigationGuard, useReadingFlowBackGuard } from '../lib/navigationGuard';
import { getReading } from '../lib/api';
import { getCardFrontImageSrc } from '../lib/cardImages';
import {
  generateDemoCardSelections,
  readDemoCardSelections,
  writeDemoCardSelections,
  type DemoCardSelection,
} from '../lib/demoCards';
import { SITE_CONFIG } from '../config/site';
import UiIcon from '../components/common/UiIcon';

interface CardPosition {
  id: number;
  x: number;
  y: number;
  rotation: number;
  meaning: string;
  scale?: number;
}

const getSpreadLayout = (spread: SpreadType): CardPosition[] => {
  switch (spread) {
    case 'one-card':
      return [{ id: 0, x: 50, y: 50, rotation: 0, meaning: '현재 상황', scale: 1.3 }];
    case 'three-card':
      return [
        { id: 0, x: 20, y: 50, rotation: -8, meaning: '과거' },
        { id: 1, x: 50, y: 50, rotation: 0, meaning: '현재', scale: 1.15 },
        { id: 2, x: 80, y: 50, rotation: 8, meaning: '미래' },
      ];
    case 'celtic-cross':
      return [
        { id: 0, x: 35, y: 50, rotation: 0, meaning: '현재 상황' },
        { id: 1, x: 43, y: 50, rotation: -90, meaning: '장애물', scale: 0.95 },
        { id: 2, x: 38.5, y: 20, rotation: 0, meaning: '목표/이상' },
        { id: 3, x: 38.5, y: 80, rotation: 0, meaning: '과거/기반' },
        { id: 4, x: 10, y: 50, rotation: 0, meaning: '최근 과거' },
        { id: 5, x: 67, y: 50, rotation: 0, meaning: '가까운 미래' },
        { id: 6, x: 88, y: 80, rotation: 0, meaning: '자신' },
        { id: 7, x: 88, y: 62, rotation: 0, meaning: '환경/타인' },
        { id: 8, x: 88, y: 44, rotation: 0, meaning: '희망/두려움' },
        { id: 9, x: 88, y: 26, rotation: 0, meaning: '최종 결과' },
      ];
    default:
      return [];
  }
};

const getSpreadInfo = (spread: SpreadType) => {
  switch (spread) {
    case 'one-card':
      return { title: '원카드', subtitle: '핵심 한 장 세션', description: '현재 상황에 대한 선명한 통찰' };
    case 'three-card':
      return { title: '쓰리카드', subtitle: '세 장의 흐름 세션', description: '과거, 현재, 미래의 흐름' };
    case 'celtic-cross':
      return { title: '켈틱 크로스', subtitle: '정식 리딩 세션', description: '가장 깊고 종합적인 정식 리딩' };
    default:
      return { title: '타로 스프레드', subtitle: '리딩 세션', description: '' };
  }
};

const inferSpreadFromCards = (cards?: DrawnCard[]): SpreadType | null => {
  if (!cards || cards.length === 0) return null;
  if (cards.length === 1) return 'one-card';
  if (cards.length === 10) return 'celtic-cross';
  return 'three-card';
};

export default function SpreadLayoutPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const revealIntervalRef = useRef<number | null>(null);

  const [spread, setSpread] = useState<SpreadType>('three-card');
  const [isBooting, setIsBooting] = useState(true);
  const [flippedCards, setFlippedCards] = useState<Set<number>>(new Set());
  const [isRevealingCards, setIsRevealingCards] = useState(false);
  const [revealedCount, setRevealedCount] = useState(0);
  const [demoSelections, setDemoSelections] = useState<DemoCardSelection[]>([]);
  const [serverCards, setServerCards] = useState<DrawnCard[] | null>(null);
  const [serverCardError, setServerCardError] = useState<string | null>(null);
  const shouldWarnOnLeave = Boolean(id && !id.startsWith('demo-'));

  usePaidFlowNavigationGuard(shouldWarnOnLeave);
  useReadingFlowBackGuard(shouldWarnOnLeave);

  const clearRevealInterval = () => {
    if (revealIntervalRef.current !== null) {
      window.clearInterval(revealIntervalRef.current);
      revealIntervalRef.current = null;
    }
  };

  useEffect(() => {
    const storedSpread = sessionStorage.getItem('selected_spread') as SpreadType | null;
    if (storedSpread) setSpread(storedSpread);

    const timer = window.setTimeout(() => setIsBooting(false), 1200);
    return () => {
      window.clearTimeout(timer);
      clearRevealInterval();
    };
  }, []);

  useEffect(() => {
    if (!id?.startsWith('demo-')) {
      setDemoSelections([]);
      return;
    }

    const cachedSelections = readDemoCardSelections(id, spread);
    if (cachedSelections) {
      setDemoSelections(cachedSelections);
      return;
    }

    const generatedSelections = generateDemoCardSelections(`${id}:${spread}`, spread);
    writeDemoCardSelections(id, generatedSelections);
    setDemoSelections(generatedSelections);
  }, [id, spread]);

  useEffect(() => {
    if (!id || id.startsWith('demo-')) {
      setServerCards(null);
      setServerCardError(null);
      return;
    }

    const hasOwnerSession = true;

    if (!hasOwnerSession) {
      setServerCards(null);
      setServerCardError('리딩 접근 정보가 없습니다.');
      return;
    }

    let disposed = false;

    const fetchReadingCards = async () => {
      try {
        const response = await getReading(id);
        if (disposed) return;

        const cards = response.reading.cards || [];
        setServerCards(cards);
        setServerCardError(null);

        const inferredSpread = response.reading.spread || inferSpreadFromCards(cards);
        if (inferredSpread) {
          setSpread(inferredSpread);
          sessionStorage.setItem('selected_spread', inferredSpread);
        }
      } catch (error) {
        if (disposed) return;
        setServerCards(null);
        setServerCardError((error as Error).message || '카드 정보를 불러오지 못했습니다.');
      }
    };

    fetchReadingCards();

    return () => {
      disposed = true;
    };
  }, [id]);

  const spreadInfo = getSpreadInfo(spread);
  const positions = getSpreadLayout(spread);
  const totalCards = positions.length;
  const allCardsRevealed = revealedCount >= totalCards;
  const isServerReading = Boolean(id && !id.startsWith('demo-'));
  const shouldWaitForServerCards = isServerReading && !serverCards && !serverCardError;
  const isThreeCardSpread = spread === 'three-card';
  const cardFrameClass = spread === 'celtic-cross'
    ? 'relative h-28 w-16 touch-manipulation xs:h-30 xs:w-18 sm:h-32 sm:w-20 md:h-40 md:w-24'
    : isThreeCardSpread
      ? 'relative h-32 w-20 touch-manipulation xs:h-36 xs:w-24 sm:h-44 sm:w-28 md:h-48 md:w-32 lg:h-56 lg:w-36'
      : 'relative h-36 w-20 touch-manipulation xs:h-40 xs:w-24 sm:h-48 sm:w-28 md:h-56 md:w-32';

  const handleRevealCards = () => {
    if (!id || shouldWaitForServerCards || isRevealingCards) return;

    if (allCardsRevealed) {
      navigate(`/result/${id}`);
      return;
    }

    clearRevealInterval();
    setFlippedCards(new Set());
    setRevealedCount(0);
    setIsRevealingCards(true);

    const revealOrder = positions.map((position) => position.id);
    let currentCard = 0;

    revealIntervalRef.current = window.setInterval(() => {
      if (currentCard < totalCards) {
        const cardId = revealOrder[currentCard];
        setFlippedCards((previous) => {
          const next = new Set(previous);
          next.add(cardId);
          return next;
        });
        setRevealedCount(currentCard + 1);
        currentCard += 1;
        return;
      }

      clearRevealInterval();
      setIsRevealingCards(false);
    }, 400);
  };

  const handleCardClick = (cardId: number) => {
    if (!allCardsRevealed || isRevealingCards) return;

    setFlippedCards((previous) => {
      const next = new Set(previous);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        next.add(cardId);
      }
      return next;
    });
  };

  return (
    <div
      className="relative flex min-h-screen flex-col overflow-hidden font-serif text-white selection:bg-[#bd3b0f] selection:text-white"
      style={{ background: 'radial-gradient(circle at 50% 30%, #2a221e 0%, #14110f 70%, #000000 100%)' }}
    >
      <div className="fixed inset-0 z-50 pointer-events-none bg-[url('data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22 opacity=%220.04%22/%3E%3C/svg%3E')] opacity-[0.08]" />

      <header className="absolute left-0 top-0 z-40 flex w-full items-center justify-between p-4 opacity-90 transition-opacity duration-300 hover:opacity-100 sm:p-5 md:p-6 lg:p-8">
        <Link
          to="/"
          aria-label="메인 페이지로 이동"
          className="flex items-center gap-2 transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-[#fbbf24]/60 focus:ring-offset-2 focus:ring-offset-[#14110f] sm:gap-3"
        >
          <UiIcon name="book" className="h-5 w-5 text-[#fbbf24] sm:h-6 sm:w-6" />
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#c9a092] sm:text-sm sm:tracking-[0.3em]">
            {SITE_CONFIG.serviceNameEn}
          </span>
        </Link>
      </header>

      <main className="relative z-10 flex flex-grow flex-col items-center justify-center px-4 pb-32 pt-24 sm:px-5 sm:pb-28 sm:pt-20 md:px-6 md:pb-24">
        <div className="z-30 mb-8 text-center sm:mb-10 md:mb-12">
          <h1 className="mb-2 px-4 font-serif text-2xl font-extrabold leading-tight tracking-[-0.01em] text-[#ead6bd] sm:text-3xl md:text-4xl lg:text-5xl">
            {spreadInfo.title}
          </h1>
          <p className="mt-2 text-[10px] font-medium uppercase tracking-[0.35em] text-[#fbbf24] opacity-80 xs:text-xs sm:tracking-[0.4em] md:text-sm">
            {spreadInfo.subtitle}
          </p>
          <p className="mt-2 px-4 text-xs text-[#c9a092]/70 sm:mt-3 sm:text-sm">{spreadInfo.description}</p>
        </div>

        <div
          className="relative mx-auto w-full max-w-5xl touch-manipulation"
          style={{
            height: spread === 'celtic-cross'
              ? 'clamp(350px, 60vh, 500px)'
              : isThreeCardSpread
                ? 'clamp(340px, 58vh, 460px)'
                : 'clamp(320px, 54vh, 420px)',
            perspective: '1200px',
          }}
        >
          {positions.map((position, index) => {
            const isFlipped = flippedCards.has(position.id);
            const selection = demoSelections[index];
            const serverCard = isServerReading ? serverCards?.[index] : undefined;
            const cardImageIndex = selection?.cardIndex ?? (position.id % 78);
            const effectiveRotation = isThreeCardSpread ? 0 : position.rotation;
            const cardImageSrc = serverCard
              ? getCardFrontImageSrc(serverCard.card)
              : `/cards/fronts/${cardImageIndex}.webp`;
            const orientation = serverCard?.orientation ?? selection?.orientation ?? 'upright';

            return (
              <div
                key={position.id}
                className="absolute transition-all duration-700 ease-out"
                style={{
                  left: `${position.x}%`,
                  top: `${position.y}%`,
                  transform: `translate(-50%, -50%) rotate(${effectiveRotation}deg)`,
                  '--card-rotation': `${effectiveRotation}deg`,
                  opacity: isBooting ? 0 : 1,
                  animation: `fadeInCard 0.6s ease-out ${index * 0.12}s forwards`,
                  perspective: '1000px',
                } as CSSProperties}
                onClick={() => handleCardClick(position.id)}
              >
                <div
                  className={cardFrameClass}
                  style={{
                    transformStyle: 'preserve-3d',
                    transform: isFlipped ? 'rotateY(180deg) scale(1.05)' : 'rotateY(0deg)',
                    transition: 'transform 0.6s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  }}
                >
                  <div
                    className="absolute inset-0 rounded-xl border border-[#3d2e24] bg-[#1a1816] shadow-[0_4px_20px_rgba(0,0,0,0.6)]"
                    style={{
                      transform: `scale(${position.scale || 1})`,
                      backfaceVisibility: 'hidden',
                    }}
                  >
                    <div className="absolute inset-1 flex items-center justify-center rounded-lg border border-dashed border-[#fbbf24]/20">
                      <UiIcon name="sparkles" className="h-10 w-10 text-[#fbbf24]/30" />
                    </div>
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-black/60 to-transparent" />
                  </div>

                  <div
                    className="absolute inset-0 overflow-hidden rounded-xl border border-[#c79b66]"
                    style={{
                      transform: `scale(${position.scale || 1}) rotateY(180deg)`,
                      backfaceVisibility: 'hidden',
                    }}
                  >
                    <img
                      src={cardImageSrc}
                      alt="Tarot Card"
                      className="h-full w-full object-cover"
                      style={{ transform: orientation === 'reversed' ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          <div className="pointer-events-none absolute inset-0 z-[120]">
            {positions.map((position) => {
              const labelOffset = (isThreeCardSpread ? 152 : 78) * (position.scale || 1);
              const labelRotation = isThreeCardSpread || (spread === 'celtic-cross' && position.id === 1)
                ? 0
                : -position.rotation;

              return (
                <div
                  key={`label-${position.id}`}
                  className="absolute whitespace-nowrap"
                  style={{
                    left: `${position.x}%`,
                    top: `${position.y}%`,
                    transform: `translate(-50%, -50%) translateY(${labelOffset}px) rotate(${labelRotation}deg)`,
                  }}
                >
                  <div className="inline-block rounded-full border border-[#fbbf24]/25 bg-[#1a1512]/90 px-2 py-1 shadow-[0_4px_12px_rgba(0,0,0,0.5)] backdrop-blur-sm sm:px-2.5 sm:py-1.5 md:px-3">
                    <p className="text-[8px] font-bold uppercase tracking-wide text-[#fbbf24] xs:text-[9px] sm:text-[10px] sm:tracking-wider md:text-xs">
                      {position.meaning}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-12 text-center sm:mt-16 md:mt-20">
          <p className="mb-6 px-4 text-xs tracking-wide text-[#c9a092]/60 sm:mb-8 sm:text-sm">
            선택한 카드가 배치되었습니다.
          </p>

          {shouldWaitForServerCards && (
            <p className="mb-4 text-xs text-[#fbbf24]/70 sm:text-sm">카드 정보를 불러오는 중입니다...</p>
          )}

          {serverCardError && (
            <p className="mb-4 text-xs text-red-300 sm:text-sm">{serverCardError}</p>
          )}

          <div className="animate-fadeInUp">
            <button
              onClick={handleRevealCards}
              disabled={isRevealingCards || shouldWaitForServerCards}
              className="group mx-auto flex touch-manipulation items-center gap-2 rounded-lg border border-[#c79b66]/55 bg-[#221510]/80 px-5 py-3 text-[10px] uppercase tracking-[0.18em] text-amber-100 transition-colors hover:border-[#d2aa78] hover:bg-[#2a1b12] hover:text-white active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 xs:text-xs sm:gap-3 sm:px-6 sm:py-3.5 sm:tracking-[0.2em]"
            >
              <span className="cta-sigil" />
              {shouldWaitForServerCards ? '카드 준비 중...' : allCardsRevealed ? '리딩 시작하기' : '카드 펼치기'}
            </button>
          </div>
        </div>
      </main>

      <style>{`
        @keyframes fadeInCard {
          from { opacity: 0; transform: translate(-50%, -40%) rotate(var(--card-rotation, 0deg)) scale(0.8); }
          to { opacity: 1; transform: translate(-50%, -50%) rotate(var(--card-rotation, 0deg)) scale(1); }
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out 1.5s forwards;
          opacity: 0;
        }

        .cta-sigil {
          position: relative;
          width: 2rem;
          height: 1px;
          background: #fbbf24;
          opacity: 0.9;
          transition: transform 0.4s ease;
        }

        .cta-sigil::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          width: 0.35rem;
          height: 0.35rem;
          transform: translate(-2px, -50%);
          border-radius: 9999px;
          background: #fbbf24;
          transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .group:hover .cta-sigil::after {
          transform: translate(26px, -50%);
        }
      `}</style>
    </div>
  );
}
